import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { X, Plus } from 'lucide-react';

export type TimeRange = { open: string; close: string };
export type HoursState = Record<string, TimeRange[]>;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

interface GymHoursEditorProps {
  value: HoursState;
  onChange: (next: HoursState) => void;
}

/**
 * Controlled editor for a location's weekly opening hours. Extracted from the
 * gym settings page so both that page and the gym-admin app share one source
 * of truth. The parent owns the state and the save action.
 */
export function GymHoursEditor({ value, onChange }: GymHoursEditorProps) {
  const { t } = useTranslation();

  const toggleDay = (day: string) => {
    if (value[day]) {
      const next = { ...value };
      delete next[day];
      onChange(next);
    } else {
      onChange({ ...value, [day]: [{ open: '09:00', close: '18:00' }] });
    }
  };

  const addTimeRange = (day: string) => {
    onChange({ ...value, [day]: [...(value[day] || []), { open: '09:00', close: '18:00' }] });
  };

  const removeTimeRange = (day: string, index: number) => {
    const updated = value[day].filter((_, i) => i !== index);
    if (updated.length === 0) {
      const next = { ...value };
      delete next[day];
      onChange(next);
    } else {
      onChange({ ...value, [day]: updated });
    }
  };

  const updateTimeRange = (day: string, index: number, field: 'open' | 'close', time: string) => {
    onChange({
      ...value,
      [day]: value[day].map((range, i) => (i === index ? { ...range, [field]: time } : range)),
    });
  };

  return (
    <div className="space-y-3">
      <Label>{t('gymSettings.openingHours')}</Label>
      <div className="space-y-2">
        {DAYS.map((day) => {
          const isEnabled = Boolean(value[day]);
          return (
            <div key={day} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id={`day-${day}`}
                  checked={isEnabled}
                  onChange={() => toggleDay(day)}
                  className="h-4 w-4 cursor-pointer"
                />
                <label
                  htmlFor={`day-${day}`}
                  className="font-medium cursor-pointer select-none"
                >
                  {t(`gymSettings.${day}`)}
                </label>
              </div>

              {isEnabled && (
                <div className="ml-7 space-y-2">
                  {value[day].map((range, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground w-12">{t('gymSettings.open')}</span>
                      <Input
                        type="time"
                        value={range.open}
                        onChange={(e) => updateTimeRange(day, index, 'open', e.target.value)}
                        className="w-32"
                      />
                      <span className="text-sm text-muted-foreground w-12">{t('gymSettings.close')}</span>
                      <Input
                        type="time"
                        value={range.close}
                        onChange={(e) => updateTimeRange(day, index, 'close', e.target.value)}
                        className="w-32"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive p-1"
                        onClick={() => removeTimeRange(day, index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addTimeRange(day)}
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t('gymSettings.addTimeRange')}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
